import { useRef, useState, useEffect } from "react";
import { IoIosClose } from "react-icons/io";
import { Combobox } from "@headlessui/react";
import { FaChevronDown } from "react-icons/fa";
import { useIntersectionObserver } from "usehooks-ts";
import { debounce } from "lodash";
import { FaSpinner } from "react-icons/fa6";

const AsyncSelect = ({
  label,
  options = [],
  hasMore,
  fetchNext,
  defaultLabel = "",
  isLoading,
  value,
  onChange,
  valueAsObject = true,
  placeholder,
  isMultiple = false,
}) => {
  const [query, setQuery] = useState("");
  const [isFocused, setIsFocused] = useState(true);
  const [selectedLabels, setSelectedLabels] = useState([]);
  // Add ref to store value-to-label mapping
  const selectedLabelMapRef = useRef({});

  const { ref } = useIntersectionObserver({
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (isIntersecting) {
        if (!isLoading) {
          fetchNext?.(query);
        }
      }
    },
  });

  // Update selectedLabels using the ref and fallback if option not present
  useEffect(() => {
    if (isMultiple && Array.isArray(value)) {
      const newLabels = value.map((val) => {
        const opt = options.find((el) => el.value === val);
        if (opt) {
          selectedLabelMapRef.current[val] = opt.label;
          return opt.label;
        }
        return selectedLabelMapRef.current[val] || "";
      });
      setSelectedLabels(newLabels);
      // setSelectedLabels([
      //   "ABC label",
      //   "DEF label",
      //   "GHI label",
      //   "JKL label",
      //   "MNO label",
      //   "PQR label",
      //   "STU label",
      //   "VWX label",
      // ]);
    } else {
      const opt = options.find((el) => el.value === value);
      const label = opt ? opt.label : selectedLabelMapRef.current[value] || "";
      selectedLabelMapRef.current[value] = label;
      setSelectedLabels([label]);
    }
  }, [value, isMultiple, options]); // added options as dependency

  const onFocus = () => {
    setIsFocused(true);
  };

  const onFocusOut = () => {
    if (value === "" && value === undefined && value === null) {
      setIsFocused(false);
    }
  };

  const debouncedOnSearch = useRef(
    debounce((query) => {
      onSearch(query);
    }, 1000)
  ).current;

  const onSearch = (query) => {
    setQuery(query);
    fetchNext?.(query, true);
  };

  const handleSelect = (val) => {
    onChange(val);
    if (isMultiple) {
      const newLabels = val.map((v) => {
        const opt = options.find((el) => el.value === v);
        const label = opt ? opt.label : selectedLabelMapRef.current[v] || "";
        selectedLabelMapRef.current[v] = label;
        return label;
      });
      setSelectedLabels(newLabels);
    } else {
      const opt = options.find((el) => el.value === val);
      const label = opt ? opt.label : selectedLabelMapRef.current[val] || "";
      selectedLabelMapRef.current[val] = label;
      setSelectedLabels([label]);
    }
  };

  const handleRemove = (val) => {
    const newValue = value.filter((v) => v !== val);
    onChange(newValue);
    const newLabels = selectedLabels.filter(
      (label, index) => value[index] !== val
    );
    setSelectedLabels(newLabels);
  };

  return (
    <Combobox
      as={"div"}
      className={"relative"}
      value={value}
      by={valueAsObject ? "value" : null}
      onChange={handleSelect}
      multiple={isMultiple}
    >
      <div className="border rounded-md">
        <div className={`flex items-center flex-wrap gap-2`}>
          {isMultiple &&
            Array.isArray(value) &&
            value.map((val, index) => (
              <span
                key={val}
                className="py-0.5 px-2 rounded-full bg-cyan-800/20  font-sans font-semibold text-sm"
              >
                <div className="flex justify-center items-center">
                  <span className="text-primary">
                    {valueAsObject ? val.label : selectedLabels[index]}
                  </span>
                  <button type="button" onClick={() => handleRemove(val)}>
                    <IoIosClose className="h-5 w-5 text-primary" />
                  </button>
                </div>
              </span>
            ))}

          <Combobox.Input
            aria-label=""
            className={
              " rounded-lg bg-white py-1.5 pr-[2rem] pl-2 focus:outline-none grow "
            }
            displayValue={(value) => {
              if (!isMultiple) {
                if (valueAsObject) {
                  return value.label;
                } else {
                  const option = options?.find((el) => el.value === value);
                  return option?.label ? option?.label : defaultLabel;
                }
              }
              return "";
            }}
            onChange={(event) => {
              debouncedOnSearch(event.target.value);
            }}
            onFocus={onFocus}
            onBlur={onFocusOut}
            placeholder={placeholder}
          />
          <Combobox.Button
            type="button"
            className="group absolute bottom-3 right-0 mr-1.5 px-[0.5rem]"
          >
            <FaChevronDown className={"w-3 h-3"} />
          </Combobox.Button>
        </div>
      </div>

      <Combobox.Options
        anchor="bottom"
        className="absolute w-full z-[1500] empty:invisible !max-h-[20rem] p-[0.50rem] bg-white border border-gray-300 overflow-auto"
      >
        {/* w-[var(--input-width)] */}
        {options.map((opt, idx) => (
          <Combobox.Option
            key={opt.value + "_" + idx}
            value={valueAsObject ? opt : opt?.value}
            className="data-[focus]:bg-secondary/20 py-[0.25rem]"
          >
            {opt?.label}
          </Combobox.Option>
        ))}
        {options.length === 0 && !isLoading && (
          <p className="text-neutral text-center w-full py-[1.25rem]">
            No options found
          </p>
        )}
        {isLoading && (
          <div className="grid place-items-center">
            <FaSpinner className={"w-7 h-7"} />
          </div>
        )}
        {hasMore && !isLoading && (
          <div ref={ref} className="w-full py-[1.25rem]"></div>
        )}
      </Combobox.Options>
    </Combobox>
  );
};

export default AsyncSelect;
